import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Calendar, Clock, CheckCircle, Video, User, Mail, Phone, AlertTriangle } from 'lucide-react';
import CalendlyStyleCalendar from '../components/CalendlyStyleCalendar';
import { getLocalTimezone, convertFromIST, formatTimeDisplay, getTimezoneAbbreviation } from '../utils/timezone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DiscoveryCall = () => {
  const [step, setStep] = useState(1);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedSlotRaw, setSelectedSlotRaw] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [bookingData, setBookingData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [bookingResult, setBookingResult] = useState(null);
  const [clientTimezone, setClientTimezone] = useState('Asia/Kolkata');
  const [timezoneAbbr, setTimezoneAbbr] = useState('IST');
  const [bookingUnavailable, setBookingUnavailable] = useState(null);

  useEffect(() => {
    checkBookingAvailability();
    const detectedTz = getLocalTimezone();
    setClientTimezone(detectedTz);
    setTimezoneAbbr(getTimezoneAbbreviation(detectedTz));
    fetchAvailableSlots(detectedTz);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkBookingAvailability = async () => {
    try {
      const res = await axios.get(`${API}/booking-status`);
      if (!res.data.available) {
        setBookingUnavailable(res.data.message);
      }
    } catch (err) {
      console.error('Booking availability check failed:', err);
    }
  };

  const fetchAvailableSlots = async (tz) => {
    setLoadingSlots(true);
    try {
      const today = new Date();
      const fromDate = today.toISOString().split('T')[0];
      const toDate = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      const response = await axios.get(`${API}/discovery/available-dates`, {
        params: { from_date: fromDate, to_date: toDate }
      });

      const timezone = tz || clientTimezone;
      const slotsWithLocalTime = response.data.map(slot => {
        const converted = convertFromIST(slot.date, slot.time, timezone);
        return {
          ...slot,
          time_ist: slot.time,
          time_local: converted.time,
          display_date: converted.date,
          date_changed: converted.dateChanged
        };
      });
      setAvailableSlots(slotsWithLocalTime);
    } catch (error) {
      console.error('Error fetching slots:', error);
      toast.error('Failed to load available slots');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSlotSelect = (date, time) => {
    setSelectedDate(date);
    setSelectedTime(time);
    if (date && time) {
      const slot = availableSlots.find(s => s.date === date && s.time_ist === time);
      setSelectedSlotRaw(slot);
    } else {
      setSelectedSlotRaw(null);
    }
  };

  const handleNext = () => {
    if (step === 1) {
      if (!selectedDate || !selectedTime) {
        toast.error('Please select a date and time');
        return;
      }
      setStep(2);
    }
  };

  const handleSubmit = async () => {
    if (!bookingData.name || !bookingData.email || !bookingData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/discovery/book`, {
        ...bookingData,
        session_date: selectedDate,
        session_time: selectedTime
      });

      setBookingResult(response.data);
      setStep(3);
      toast.success('Discovery call booked successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book discovery call');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const stepLabels = ['Select Date & Time', 'Your Details', 'Confirmation'];

  return (
    <div className="min-h-screen pt-20 px-6 py-16" data-testid="discovery-call-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-medium text-foreground mb-3">
            Book a Discovery Call
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            A free 15-minute call to discuss your needs and see if we're a good fit for working together.
          </p>
        </div>

        {/* Booking Unavailable Banner */}
        {bookingUnavailable && (
          <div className="mb-8 p-6 bg-red-50 border-2 border-red-200 rounded-2xl text-center" data-testid="booking-unavailable-banner">
            <AlertTriangle size={32} className="mx-auto text-red-500 mb-3" />
            <h2 className="text-xl font-heading font-medium text-red-800 mb-2">Booking Temporarily Unavailable</h2>
            <p className="text-red-600">{bookingUnavailable}</p>
          </div>
        )}

        {!bookingUnavailable && (
          <>
        {/* Progress Steps - matching BookSession style */}
        <div className="flex justify-between mb-12">
          {stepLabels.map((label, idx) => (
            <div key={label} className="flex flex-col items-center flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                step > idx + 1 ? 'bg-primary text-white' : step === idx + 1 ? 'bg-accent text-white' : 'bg-muted text-muted-foreground'
              }`}>
                {step > idx + 1 ? <CheckCircle size={20} /> : idx + 1}
              </div>
              <p className="text-sm mt-2 text-center text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>

        {/* Step 1: Select Date & Time */}
        {step === 1 && (
          <Card className="bg-white/80 backdrop-blur-sm rounded-3xl p-8" data-testid="select-slot-step">
            <h2 className="text-3xl font-heading font-medium text-foreground mb-2">Select Date & Time</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Pick a date to see available times for your 15-minute discovery call
              {clientTimezone !== 'Asia/Kolkata' && (
                <span> (times shown in {timezoneAbbr})</span>
              )}
            </p>

            {loadingSlots ? (
              <div className="text-center py-12 bg-secondary/30 rounded-2xl">
                <div className="animate-spin w-8 h-8 border-3 border-primary border-t-transparent rounded-full mx-auto"></div>
                <p className="mt-4 text-muted-foreground">Loading available slots...</p>
              </div>
            ) : availableSlots.length === 0 ? (
              <div className="text-center py-12 bg-secondary/30 rounded-2xl">
                <Calendar size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No available slots in the next 3 months.</p>
                <p className="text-sm text-muted-foreground mt-2">Please check back later or contact us directly.</p>
              </div>
            ) : (
              <CalendlyStyleCalendar
                availableSlots={availableSlots}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                onSelectSlot={handleSlotSelect}
                clientTimezone={clientTimezone}
                timezoneAbbr={timezoneAbbr}
              />
            )}

            {selectedDate && selectedTime && (
              <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-xl">
                <p className="text-sm font-medium text-foreground flex items-center gap-2">
                  <CheckCircle size={18} className="text-primary" />
                  Selected: {formatDate(selectedDate)} at {formatTimeDisplay(selectedSlotRaw?.time_local || selectedTime)} {timezoneAbbr}
                </p>
              </div>
            )}

            <Button
              onClick={handleNext}
              disabled={!selectedDate || !selectedTime}
              className="w-full mt-6 bg-primary text-white hover:bg-primary/90 rounded-full py-6 text-lg disabled:opacity-50"
              data-testid="continue-to-details"
            >
              Continue
            </Button>
          </Card>
        )}

        {/* Step 2: Your Details */}
        {step === 2 && (
          <Card className="bg-white/80 backdrop-blur-sm rounded-3xl p-8" data-testid="details-step">
            <h2 className="text-3xl font-heading font-medium text-foreground mb-2">Your Details</h2>
            <p className="text-muted-foreground mb-6">Tell us a bit about yourself.</p>

            {/* Selected Slot Summary */}
            <div className="bg-secondary/50 rounded-2xl p-4 mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar size={20} className="text-primary" />
                <span className="text-foreground">
                  {formatDate(selectedDate)}
                </span>
              </div>
              <div className="flex items-center gap-2 text-primary">
                <Clock size={16} />
                <span className="font-medium">{formatTimeDisplay(selectedSlotRaw?.time_local || selectedTime)} {timezoneAbbr}</span>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <Label className="text-foreground font-medium flex items-center gap-2">
                  <User size={20} /> Full Name *
                </Label>
                <Input
                  value={bookingData.name}
                  onChange={(e) => setBookingData({...bookingData, name: e.target.value})}
                  placeholder="Your full name"
                  className="mt-2 bg-white/50 border-muted rounded-lg"
                  data-testid="name-input"
                />
              </div>

              <div>
                <Label className="text-foreground font-medium flex items-center gap-2">
                  <Mail size={20} /> Email Address *
                </Label>
                <Input
                  type="email"
                  value={bookingData.email}
                  onChange={(e) => setBookingData({...bookingData, email: e.target.value})}
                  placeholder="your@email.com"
                  className="mt-2 bg-white/50 border-muted rounded-lg"
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label className="text-foreground font-medium flex items-center gap-2">
                  <Phone size={20} /> Phone Number *
                </Label>
                <Input
                  value={bookingData.phone}
                  onChange={(e) => setBookingData({...bookingData, phone: e.target.value})}
                  placeholder="+91 98765 43210"
                  className="mt-2 bg-white/50 border-muted rounded-lg"
                  data-testid="phone-input"
                />
              </div>

              <div>
                <Label className="text-foreground font-medium">What would you like to discuss? (Optional)</Label>
                <Textarea
                  value={bookingData.notes}
                  onChange={(e) => setBookingData({...bookingData, notes: e.target.value})}
                  placeholder="Briefly share what brings you to therapy..."
                  className="mt-2 bg-white/50 border-muted rounded-lg min-h-24"
                  data-testid="notes-input"
                />
              </div>
            </div>

            <div className="flex gap-4 mt-8">
              <Button
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1 rounded-full py-6"
                data-testid="back-button"
              >
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-primary text-white hover:bg-primary/90 rounded-full py-6"
                data-testid="book-call-button"
              >
                {loading ? 'Booking...' : 'Book Discovery Call'}
              </Button>
            </div>
          </Card>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && (
          <Card className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 text-center" data-testid="confirmation-step">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-green-600" />
            </div>

            <h2 className="text-3xl font-heading font-medium text-foreground mb-2">You're All Set!</h2>
            <p className="text-muted-foreground mb-8">Your discovery call has been booked. Check your email for the calendar invite.</p>

            <div className="bg-secondary/50 rounded-2xl p-6 mb-8 text-left">
              <h3 className="font-medium text-foreground mb-4">Call Details</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Calendar size={18} className="text-primary" />
                  <span className="text-foreground">{formatDate(selectedDate)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock size={18} className="text-primary" />
                  <span className="text-foreground">{formatTimeDisplay(selectedSlotRaw?.time_local || selectedTime)} {timezoneAbbr} (15 minutes)</span>
                </div>
                <div className="flex items-center gap-3">
                  <Video size={18} className="text-primary" />
                  <span className="text-foreground">Google Meet (link in calendar invite)</span>
                </div>
              </div>
            </div>

            <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 text-left">
              <h3 className="font-medium text-foreground mb-2">What's Next?</h3>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>Check your email for the calendar invite with Google Meet link</li>
                <li>Add the event to your calendar</li>
                <li>Find a quiet, private space for the call</li>
                <li>We'll connect at the scheduled time!</li>
              </ul>
            </div>

            <Button
              onClick={() => window.location.href = '/'}
              className="mt-8 bg-primary text-white hover:bg-primary/90 rounded-full px-8 py-6"
              data-testid="back-to-home"
            >
              Back to Home
            </Button>
          </Card>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default DiscoveryCall;
